import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocalPharmacyRounded from '@mui/icons-material/LocalPharmacyRounded';
import RateReviewRounded from '@mui/icons-material/RateReviewRounded';
import {
  fetchSpecialtiesMetadata,
  searchDrugsBySpecialty,
  subscribePendingQueueItems,
} from '../services/drugReferenceService';
import { EMERGENCY_MEDICINE_SPECIALTY, REVIEW_STATUS } from '../constants/collections';
import SpecialtyManager from './SpecialtyManager';

export default function DrugReferenceDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [specialtiesMeta, setSpecialtiesMeta] = useState(null);
  const [drugs, setDrugs] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meta, drugList] = await Promise.all([
          fetchSpecialtiesMetadata(),
          searchDrugsBySpecialty(EMERGENCY_MEDICINE_SPECIALTY),
        ]);
        if (!cancelled) {
          setSpecialtiesMeta(meta);
          setDrugs(drugList);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load drug reference data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unsub = subscribePendingQueueItems(
      (items) => setPendingCount(items.length),
      (err) => console.error('[DrugReferenceDashboard] queue subscribe failed', err)
    );
    return () => unsub();
  }, []);

  const filteredDrugs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return drugs.filter((drug) => {
      const matchesQuery =
        !q ||
        drug.genericName?.toLowerCase().includes(q) ||
        drug.id?.toLowerCase().includes(q) ||
        (drug.alternateNames || []).some((n) => n.toLowerCase().includes(q));
      const matchesStatus =
        statusFilter === 'all' || drug.reviewStatus === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [drugs, query, statusFilter]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <LocalPharmacyRounded color="primary" />
        <Typography variant="h5">Drug Reference</Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <SpecialtyManager specialtiesMeta={specialtiesMeta} drugCount={drugs.length} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, mt: 3 }}>
        <TextField
          label="Search drugs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
        />
        <TextField
          select
          label="Review status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          SelectProps={{ native: true }}
          sx={{ minWidth: 180 }}
        >
          <option value="all">All</option>
          <option value={REVIEW_STATUS.APPROVED}>Approved</option>
          <option value={REVIEW_STATUS.PENDING}>Pending</option>
          <option value={REVIEW_STATUS.REJECTED}>Rejected</option>
        </TextField>
        <Button
          component={RouterLink}
          to="/dashboard/drug-reference/review"
          variant="outlined"
          startIcon={<RateReviewRounded />}
        >
          Review queue ({pendingCount})
        </Button>
      </Stack>

      {filteredDrugs.length === 0 ? (
        <Typography color="text.secondary">
          No drugs found. Run discovery (Prompt 2) to populate the EM set.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {filteredDrugs.map((drug) => (
            <Card key={drug.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ sm: 'center' }}
                  spacing={1}
                >
                  <Box>
                    <Typography variant="subtitle1">{drug.genericName || drug.id}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {drug.emRelevance && <Chip size="small" label={drug.emRelevance} />}
                      {drug.reviewStatus && (
                        <Chip
                          size="small"
                          color={drug.reviewStatus === REVIEW_STATUS.APPROVED ? 'success' : 'default'}
                          label={drug.reviewStatus}
                        />
                      )}
                      {drug.highAlert && <Chip size="small" color="error" label="High alert" />}
                    </Stack>
                  </Box>
                  <Button component={RouterLink} to={`/dashboard/drug-reference/drug/${drug.id}`}>
                    View card
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
